import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export interface ExpenseCategory {
  value: string;
  label: string;
  icon_path: string | null;
  is_refueling: boolean;
  is_service: boolean;
  sort_order: number;
}

interface ExpenseCategoriesContextType {
  categories: ExpenseCategory[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const ExpenseCategoriesContext = createContext<ExpenseCategoriesContextType | undefined>(undefined);

export const useExpenseCategories = () => {
  const context = useContext(ExpenseCategoriesContext);
  if (context === undefined) {
    throw new Error('useExpenseCategories must be used within an ExpenseCategoriesProvider');
  }
  return context;
};

interface ExpenseCategoriesProviderProps {
  children: React.ReactNode;
}

export const ExpenseCategoriesProvider: React.FC<ExpenseCategoriesProviderProps> = ({ children }) => {
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('expense_categories')
        .select('*')
        .order('sort_order', { ascending: true });

      if (fetchError) {
        // If table doesn't exist, use fallback categories
        if (fetchError.code === 'PGRST116' || fetchError.message?.includes('relation "expense_categories" does not exist')) {
          console.warn('expense_categories table not found, using fallback categories');
          setCategories(getFallbackCategories());
          return;
        }
        throw fetchError;
      }

      console.log('Fetched categories from database:', data?.length || 0);
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching expense categories:', err);
      // Use fallback categories on any error
      console.log('Using fallback categories due to error');
      setCategories(getFallbackCategories());
      setError(null); // Don't show error for fallback
    } finally {
      setLoading(false);
    }
  };

  const getFallbackCategories = (): ExpenseCategory[] => [
    { value: "Car Wash", label: "Car Wash", icon_path: "/icons/ExpenseIcons/CarWash.png", is_refueling: false, is_service: false, sort_order: 1 },
    { value: "Insurance", label: "Insurance", icon_path: "/icons/ExpenseIcons/Insurance.png", is_refueling: false, is_service: false, sort_order: 2 },
    { value: "Fine", label: "Fine", icon_path: "/icons/ExpenseIcons/Fine.png", is_refueling: false, is_service: false, sort_order: 3 },
    { value: "Toll", label: "Toll", icon_path: "/icons/ExpenseIcons/Toll.png", is_refueling: false, is_service: false, sort_order: 4 },
    { value: "Tax", label: "Tax", icon_path: "/icons/ExpenseIcons/Tax.png", is_refueling: false, is_service: false, sort_order: 5 },
    { value: "Wiper Blades", label: "Wiper Blades", icon_path: "/icons/ExpenseIcons/WiperBlades.png", is_refueling: false, is_service: false, sort_order: 6 },
    { value: "Tires", label: "Tires", icon_path: "/icons/ExpenseIcons/Tires.png", is_refueling: false, is_service: false, sort_order: 7 },
    { value: "Tuning", label: "Tuning", icon_path: "/icons/ExpenseIcons/Tuning.png", is_refueling: false, is_service: false, sort_order: 8 },
    { value: "Accessories", label: "Accessories", icon_path: "/icons/ExpenseIcons/Accessories.png", is_refueling: false, is_service: false, sort_order: 9 },
    { value: "Parking", label: "Parking", icon_path: "/icons/ExpenseIcons/Parking.png", is_refueling: false, is_service: false, sort_order: 10 },
    { value: "Refueling", label: "Refueling", icon_path: "/icons/ExpenseIcons/Refueling.png", is_refueling: true, is_service: false, sort_order: 11 },
    { value: "Financing", label: "Financing", icon_path: "/icons/ExpenseIcons/Financing.png", is_refueling: false, is_service: false, sort_order: 12 },
    { value: "Car Inspection", label: "Car Inspection", icon_path: "/icons/ExpenseIcons/CarInspection.png", is_refueling: false, is_service: false, sort_order: 13 },
    { value: "Other", label: "Other", icon_path: "/icons/ExpenseIcons/Other.png", is_refueling: false, is_service: false, sort_order: 14 }
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <ExpenseCategoriesContext.Provider 
      value={{ 
        categories, 
        loading, 
        error, 
        refetch: fetchCategories 
      }}
    >
      {children}
    </ExpenseCategoriesContext.Provider>
  );
};
